"use client";

import React, { useEffect, useRef, useState } from "react";
import { CheckCircle } from "lucide-react";

/**
 * Trust framing — why this review reflects a real hiring manager's read.
 *
 * Pinned scroll-story matching the reference design: while the section is
 * pinned, the orange vertical meter fills and the card column scrolls so the
 * two-card window advances through all six reasons; the pin then releases.
 *
 * Dimensions are taken from the 1440×1003 reference. The card height scales
 * with the viewport (~24vh, the reference's 240/1003 ratio, clamped) so the
 * proportions hold on any screen height instead of stretching or leaving dead
 * space. Falls back to a plain stack before hydration / under reduced motion.
 */

const REASONS = [
  {
    title: "Real hiring experience",
    desc: "We read portfolios for founders who are hiring. You get the same read, on your own work.",
  },
  {
    title: "Evidence from your work",
    desc: "Feedback points at what's on the page. No vague vibes. You see what earned the reaction.",
  },
  {
    title: "Logos don't carry it",
    desc: "Prestige is ignored. Your work is judged on what it shows, not where you worked.",
  },
  {
    title: "Matched to your level",
    desc: "Expectations fit your seniority, junior to lead, so the notes stay fair and useful.",
  },
  {
    title: "A clear next move",
    desc: "You leave knowing if you're interview-ready, and what stands between you and the next tier.",
  },
  {
    title: "Written to act on",
    desc: "Flags for what to keep, sharpen, and fix before you apply again.",
  },
];

const TICKS = 44;
const CARD_GAP = 32; // px — kept in sync with the `space-y-8` class
const VISIBLE = 2; // cards shown in the pinned window at once
const CARD_VH = 0.24; // reference: 240px card at 1003px viewport
const MIN_CARD_H = 210;
const MAX_CARD_H = 300;

/** Vertical segmented scroll meter — fills top→bottom with progress (0–1). */
const ScrollMeter: React.FC<{ progress: number; height: number }> = ({
  progress,
  height,
}) => (
  <div className="flex flex-col justify-between" style={{ height }}>
    {Array.from({ length: TICKS }).map((_, i) => {
      const on = i < progress * TICKS;
      return (
        <span
          key={i}
          className="h-[3px] w-6 rounded-full"
          style={{
            backgroundColor: on ? "#F97316" : "#33201d",
            opacity: on ? 1 - (i / TICKS) * 0.4 : 0.55,
            transition: "background-color 0.15s ease, opacity 0.15s ease",
          }}
        />
      );
    })}
  </div>
);

const ReasonCard: React.FC<{
  title: string;
  desc: string;
  height?: number;
}> = ({ title, desc, height }) => (
  <div
    className="flex flex-col justify-center rounded-[28px] border border-[#20120f] bg-[#0B0304] px-8 md:px-12"
    style={height ? { height } : { paddingTop: 36, paddingBottom: 36 }}
  >
    <CheckCircle className="h-7 w-7 text-[#F97316]" />
    <h3 className="mt-7 text-xl md:text-2xl font-prociono text-white">
      {title}
    </h3>
    <p className="mt-3 max-w-xl text-sm md:text-base text-[#9a8f86] leading-relaxed">
      {desc}
    </p>
  </div>
);

export const PRWhyTrust: React.FC = () => {
  const outerRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [cardHeight, setCardHeight] = useState(MIN_CARD_H);
  const [progress, setProgress] = useState(0);

  // Enable + size cards to the viewport. The pinned effect only runs on md+
  // pointers/screens — on mobile the 100vh-based pin is janky (address-bar
  // resizes), so we fall back to a plain stacked layout. Also skipped for
  // reduced motion.
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const apply = () => {
      const shouldEnable = !reduce && window.innerWidth >= 768;
      setEnabled(shouldEnable);
      if (shouldEnable) {
        const raw = window.innerHeight * CARD_VH;
        setCardHeight(Math.max(MIN_CARD_H, Math.min(raw, MAX_CARD_H)));
      }
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  const windowH = VISIBLE * cardHeight + (VISIBLE - 1) * CARD_GAP;
  const maxShift =
    REASONS.length * cardHeight + (REASONS.length - 1) * CARD_GAP - windowH;

  // Track scroll progress through the tall section while it's pinned.
  useEffect(() => {
    if (!enabled) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const el = outerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total, 0));
      setProgress(total > 0 ? scrolled / total : 0);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled, maxShift]);

  const shift = progress * maxShift;

  return (
    <section
      ref={outerRef}
      className="relative bg-[#070203]"
      style={enabled ? { height: `calc(100vh + ${maxShift}px)` } : undefined}
    >
      <div
        className={
          enabled ? "sticky top-0 h-screen overflow-hidden" : "py-16 md:py-24"
        }
      >
        <div
          className={
            enabled
              ? "mx-auto max-w-5xl px-5 md:px-8 pt-[12vh]"
              : "mx-auto max-w-5xl px-5 md:px-8"
          }
        >
          {/* Heading */}
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl md:text-5xl font-prociono text-white leading-tight">
              Why you can <span className="text-[#F97316]">trust the read</span>.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm md:text-base text-[#cbbfb5]">
              Not generic tips or a friend being nice. A live read from
              someone who hires designers.
            </p>
          </div>

          {/* Meter + card window, centered as a group */}
          <div className="mt-[8vh] flex justify-center gap-10 md:gap-16">
            {enabled && (
              <div className="hidden shrink-0 pt-1 md:block">
                <ScrollMeter progress={progress} height={windowH} />
              </div>
            )}

            <div
              className={
                enabled
                  ? "relative w-full max-w-[800px] overflow-hidden"
                  : "relative w-full max-w-[800px]"
              }
              style={enabled ? { height: windowH } : undefined}
            >
              <div
                className="space-y-8"
                style={
                  enabled
                    ? {
                        transform: `translateY(${-shift}px)`,
                        willChange: "transform",
                      }
                    : undefined
                }
              >
                {REASONS.map((r) => (
                  <ReasonCard
                    key={r.title}
                    title={r.title}
                    desc={r.desc}
                    height={enabled ? cardHeight : undefined}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
